'use client';

import { Button } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Spinner } from '@shared/icons';
import { NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { ContentSwitcher } from '../form-controls/ContentSwitcher/ContentSwitcher';
import { TextInput } from '../form-controls/TextInput/TextInput';
import type { Option } from '../form-controls/types';
import { StepLayout } from '../Forms/Registration/components/StepLayout/StepLayout';
import { StatusNotification } from '../StatusNotification/StatusNotification';
import type { DepositLimitsProps } from './types';

const DIALOG_NAMESPACE = 'global_dialogs.deposit_limits_dialog';
const FORM_NAMESPACE = 'form';

function getTrackingPeriod(selectedPeriodValue: number): string {
  return selectedPeriodValue === 0 ? 'monthly' : selectedPeriodValue === 1 ? 'weekly' : 'daily';
}

function getDisplayAmount(amount: string | undefined | null): string {
  const num = Number(amount);
  if (amount === undefined || amount === null || isNaN(num) || num < 0) {
    return '';
  }
  return amount;
}

function getPeriodFromLimitPeriod(singlePeriod: 'DAY' | 'WEEK' | 'MONTH', t: (key: string) => string): { label: string; value: number } {
  const periodMap = {
    MONTH: { label: t('buttons.monthly'), value: 0 },
    WEEK: { label: t('buttons.weekly'), value: 1 },
    DAY: { label: t('buttons.daily'), value: 2 },
  } as const;
  return periodMap[singlePeriod];
}

function DepositLimits({
  value,
  onPeriodChange,
  onAmountChange,
  onAmountBlur,
  depositLimits,
  amountError,
  fieldIdPrefix = 'depositLimits',
  onApply,
  onDoItLater,
  isLoading = false,
  isApiValidating = false,
  showDoItLater = true,
  stepLayout,
  singlePeriod,
  applyLabel,
}: DepositLimitsProps) {
  const t = useTranslations(FORM_NAMESPACE);
  const tDialog = useTranslations(DIALOG_NAMESPACE);

  const effectiveValue = singlePeriod ? { ...value, period: getPeriodFromLimitPeriod(singlePeriod, t) } : value;

  const currentDailyLimit = useMemo(() => depositLimits?.limits?.find(limit => limit.limitType === 'DEPOSIT' && limit.period === 'DAY')?.limitValue ?? 0, [depositLimits]);
  const currentWeeklyLimit = useMemo(() => depositLimits?.limits?.find(limit => limit.limitType === 'DEPOSIT' && limit.period === 'WEEK')?.limitValue ?? 0, [depositLimits]);
  const currentMonthlyLimit = useMemo(() => depositLimits?.limits?.find(limit => limit.limitType === 'DEPOSIT' && limit.period === 'MONTH')?.limitValue ?? 0, [depositLimits]);
  const [limitChangeMessage, setLimitChangeMessage] = useState<string>('');
  const [limitChangeTitle, setLimitChangeTitle] = useState<string>('');

  const formatter = useCurrencyFormatter();
  const nf = NumberFormats;
  const selectedPeriod = effectiveValue.period?.label ?? t('buttons.monthly');
  const selectedPeriodValue = effectiveValue.period?.value ?? 0;
  const amount = effectiveValue.amount;

  const periodLimitMap = useMemo(() => {
    const monthly = t('buttons.monthly');
    const weekly = t('buttons.weekly');
    const daily = t('buttons.daily');
    return {
      [monthly]: currentMonthlyLimit,
      [weekly]: currentWeeklyLimit,
      [daily]: currentDailyLimit,
    };
  }, [currentDailyLimit, currentWeeklyLimit, currentMonthlyLimit, t]);

  useEffect(() => {
    if (!amount) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLimitChangeMessage('');
      setLimitChangeTitle('');
      return;
    }

    const currentLimit = periodLimitMap[selectedPeriod];
    if (!currentLimit) {
      setLimitChangeMessage('');
      setLimitChangeTitle('');
      return;
    }

    const newAmountInCents = Number(amount) * 100;
    const increasing = newAmountInCents > currentLimit;
    const actionType = increasing ? 'increasing' : 'reducing';
    const amountDifference = increasing ? newAmountInCents - currentLimit : currentLimit - newAmountInCents;
    const formattedAmountDifference = formatter(amountDifference).format(nf.WITH_DECIMALS).get();
    const formattedCurrentAmount = formatter(currentLimit).format(nf.WITH_DECIMALS).get();

    setLimitChangeMessage(
      tDialog(`${actionType}_your_limit_description`, {
        period: selectedPeriod.toLowerCase(),
        newAmount: formattedAmountDifference,
        currentAmount: formattedCurrentAmount,
      })
    );
    setLimitChangeTitle(tDialog(`${actionType}_your_limit_title`, { period: selectedPeriod.toLowerCase() }));
  }, [amount, selectedPeriod, periodLimitMap, tDialog, formatter, nf]);

  const amountLabel = useMemo(() => {
    const currentLimit = periodLimitMap[selectedPeriod];
    if (currentLimit && currentLimit > 0) {
      const formattedAmount = formatter(currentLimit).format(nf.WITH_DECIMALS).get();
      return t('labels.limit_with_current', { amount: formattedAmount });
    }
    return t('labels.limit');
  }, [selectedPeriod, periodLimitMap, t, formatter, nf]);

  const trackingPeriod = getTrackingPeriod(selectedPeriodValue);

  const dailyOption = {
    label: t('buttons.daily'),
    value: 2,
    trackingProps: { 'data-track-name': 'Daily' },
    'data-testid': 'daily',
  };
  const weeklyOption = {
    label: t('buttons.weekly'),
    value: 1,
    trackingProps: { 'data-track-name': 'Weekly' },
    'data-testid': 'weekly',
  };
  const monthlyOption = {
    label: t('buttons.monthly'),
    value: 0,
    trackingProps: { 'data-track-name': 'Monthly' },
    'data-testid': 'monthly',
  };

  const { isDailyFirstPeriodOrder } = getSiteConfig().REGISTRATION;
  const periodOptions = isDailyFirstPeriodOrder ? [dailyOption, weeklyOption, monthlyOption] : [monthlyOption, weeklyOption, dailyOption];

  const displayAmount = getDisplayAmount(effectiveValue.amount);
  const isAmountChanged = Number(displayAmount) * 100 !== (periodLimitMap[selectedPeriod] ?? 0);

  const content = (
    <>
      <div className="flex flex-col gap-8 w-full">
        {!singlePeriod && (
          <ContentSwitcher
            id={`${fieldIdPrefix}.period`}
            options={periodOptions}
            size="large"
            fullWidth
            onChange={(option: Option<number>) => onPeriodChange({ label: option.label, value: Number(option.value ?? 0) })}
            value={value.period}
            data-track-container="Limit Period"
          />
        )}

        <TextInput
          id={`${fieldIdPrefix}.amount`}
          label={amountLabel}
          type="text"
          inputMode="numeric"
          leftIcon={t('labels.currency')}
          onChange={event => onAmountChange(event.target.value.replace(/\D/g, ''))}
          onBlur={onAmountBlur}
          value={displayAmount}
          helpMessage={amountError}
          helpMessageType={amountError ? 'error' : undefined}
          inputTrackingProps={{ 'data-track-name': 'Amount' }}
          helperTrackingProps={{ 'data-track-helper': 'Amount' }}
          data-testid="amount-input"
        />
      </div>

      {limitChangeMessage && limitChangeTitle && isAmountChanged && (
        <div data-track-container="Amount">
          <StatusNotification
            label={limitChangeTitle}
            description={limitChangeMessage}
            type="info"
            dataTrackType="Deposit Limit"
            dataTrackMessage={limitChangeTitle}
            dataTrackPeriod={trackingPeriod}
          />
        </div>
      )}

      {onApply && (
        <div className="flex flex-col gap-4 mt-auto md:mt-0" data-track-container="Actions">
          <Button
            type="button"
            variant="primary"
            onClick={onApply}
            disabled={isLoading || isApiValidating || !isAmountChanged || !displayAmount}
            className="w-full h-10"
            data-testid="apply-button"
            data-track-name="Apply Limit"
            data-track-period={trackingPeriod}
          >
            {isLoading || isApiValidating ? <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} /> : (applyLabel ?? t('buttons.apply'))}
          </Button>

          {showDoItLater && onDoItLater && (
            <Button
              type="button"
              variant="secondary"
              onClick={onDoItLater}
              disabled={isLoading || isApiValidating}
              className="w-full h-10"
              data-testid="later-button"
              data-track-name="No Limits"
            >
              {tDialog('contineWithoutLimit')}
            </Button>
          )}
        </div>
      )}
    </>
  );

  if (stepLayout) {
    return <StepLayout {...stepLayout}>{content}</StepLayout>;
  }

  return content;
}

export { DepositLimits };
